import { useEffect, useState } from 'react';
import { IconClock } from '@tabler/icons-react';
import { Group, Progress, Stack, Text } from '@mantine/core';

const secondsUntil = (deadline: number | null) =>
  deadline ? Math.max(0, Math.ceil((deadline - Date.now()) / 1000)) : 0;

/** Segundos que faltam até `deadline` (ms), atualizados a cada segundo. */
export function useSecondsLeft(deadline: number | null) {
  const [secondsLeft, setSecondsLeft] = useState(() => secondsUntil(deadline));

  useEffect(() => {
    setSecondsLeft(secondsUntil(deadline));
    if (!deadline) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      const left = secondsUntil(deadline);
      setSecondsLeft(left);
      if (left === 0) {
        window.clearInterval(timer);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [deadline]);

  return secondsLeft;
}

const formatTime = (seconds: number) =>
  `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

interface OtpCountdownProps {
  secondsLeft: number;
  /** Validade total do código, para a barra de progresso. */
  totalSeconds: number;
}

/** Tempo restante do código enviado por e-mail. */
export function OtpCountdown({ secondsLeft, totalSeconds }: OtpCountdownProps) {
  const ending = secondsLeft <= 30;
  const color = ending ? 'red' : 'principal';

  return (
    <Stack gap={6} aria-live="polite">
      <Group justify="center" gap={6}>
        <IconClock size={18} color={`var(--mantine-color-${color}-6)`} />
        <Text size="sm" c={ending ? 'red' : 'dimmed'}>
          O código expira em{' '}
          <Text span fw={700} ff="monospace" c={ending ? 'red' : undefined}>
            {formatTime(secondsLeft)}
          </Text>
        </Text>
      </Group>
      <Progress
        value={totalSeconds > 0 ? (secondsLeft / totalSeconds) * 100 : 0}
        color={color}
        size="sm"
        radius="xl"
        aria-hidden
        transitionDuration={1000}
      />
    </Stack>
  );
}
